import React, { useMemo } from "react";
import Button from "@scripts/react/components/Button";
import ColorPicker from "@scripts/react/components/ColorPicker";
import { Chip } from "@scripts/react/components/Chip";
import { TooltipContainer } from "@scripts/react/components/Tooltip";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { BuildPromptSteps } from "@rapid/types/flows";
import { noop } from "lodash";
import { getChipStyle } from "@rapid/components/annotation/colors";

interface CardHeaderProps {
    name: string;
    annotationCount?: number;
    mode: BuildPromptSteps;
    flowLocked: boolean;
    isEditing: boolean;
    editValue: string;
    inputRef: React.RefObject<HTMLInputElement | null>;
    onEditClick: (e: React.MouseEvent) => void;
    onEditKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onEditBlur: () => void;
    setEditValue: (value: string) => void;
    className?: string;
    isCardHovered?: boolean;
    // Color picker props
    labelColor?: string;
    onColorChange?: (color: string) => void;
}

export const CardHeader: React.FC<CardHeaderProps> = ({
    name,
    annotationCount,
    mode,
    flowLocked,
    isEditing,
    editValue,
    inputRef,
    onEditClick,
    onEditKeyDown,
    onEditBlur,
    setEditValue,
    className,
    isCardHovered,
    labelColor,
    onColorChange
}) => {
    const chipStyle = useMemo(() => getChipStyle(labelColor), [labelColor]);

    const handleColorChange = (newColor: string) => {
        onColorChange?.(newColor);
    };

    return (
        <div className={twClassNames("relative flex items-center gap-1 px-4 py-3", className)}>
            <ColorPicker
                color={labelColor}
                onChange={handleColorChange}
                disabled={mode === BuildPromptSteps.ANNOTATE_DATA && flowLocked}
                alignment="right"
            />
            {isEditing && mode === BuildPromptSteps.ANNOTATE_DATA ? (
                <input
                    ref={inputRef}
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={onEditKeyDown}
                    onBlur={onEditBlur}
                    className="input2 xsmall !h-6 flex-1 !text-sm"
                    placeholder="Enter object name"
                    autoFocus
                />
            ) : (
                /* @ts-expect-error - Button component type issue */
                <Button
                    onClick={mode === BuildPromptSteps.ANNOTATE_DATA ? onEditClick : noop}
                    type="ghost"
                    size="xxsmall"
                    className={twClassNames(
                        "!text-sm",
                        flowLocked && "pointer-events-none",
                        mode === BuildPromptSteps.REVIEW_PREDICTIONS &&
                            "pointer-events-none cursor-default"
                    )}
                    title={mode === BuildPromptSteps.ANNOTATE_DATA ? "Edit object name" : undefined}
                >
                    {/* @ts-expect-error - TooltipContainer JSX element type mismatch */}
                    <TooltipContainer
                        title={name}
                        type="titleOnly"
                        placement="top"
                        delay={[500, 0]}
                        customButton={
                            <span
                                className={twClassNames(
                                    "block overflow-hidden text-ellipsis whitespace-nowrap break-all",
                                    isCardHovered ? "max-w-[150px]" : "max-w-[500px]"
                                )}
                            >
                                {name}
                            </span>
                        }
                    />
                </Button>
            )}
        </div>
    );
};
